<template>
  <section id="skills" class="padding-x mb-20">
    <div class="flex w-full flex-col">
      <h3
        id="what-i-do"
        v-html="whatIDo"
        class="heading-1 font-extrabold uppercase"
      ></h3>

      <div
        id="services-text"
        class="grid-gap text-flax-smoke-300 mt-[5%] grid grid-cols-12 justify-end opacity-0 lg:grid"
      >
        <p
          class="heading-6 text-flax-smoke-300/85 col-span-4 col-start-0 text-center"
        >
          ( How I Add Value )
        </p>
        <p
          class="heading-4 font-fancy col-span-8 w-full text-balance sm:font-semibold md:col-span-8"
        >
          Great user experiences are powered by strong backend systems. I design
          and build scalable, secure, and high-performance backend solutions —
          ensuring every interaction is fast, reliable, and effortless for the
          end user.
        </p>
      </div>
    </div>

    <div class="relative mt-12 w-full lg:mt-[10%]">
      <div class="mt-12 flex flex-col justify-between gap-y-16">
        <ServicesCard
          v-for="(card, index) in servicesCardProps"
          :key="index"
          :number="index + 1"
          :title="card.title"
          :body="card.body"
          :headings="card.headings"
          :shape="card.shape"
          class="border-flax-smoke-500/50 sticky border-t bg-[#0B0B0A]"
          :class="getStyle(index)"
        />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import { animateSplitText } from '@/animations';
  import { textSplitterIntoChar } from '@/functions';
  import { onBeforeMount, onMounted, ref } from 'vue';
  import { ServicesCard } from '..';

  const whatIDo = ref('My services /');

  const servicesCardProps = [
    {
      title: 'My Approach',
      body: 'I focus on building clean, scalable architectures with well-designed APIs by prioritizing simplicity, performance, security, and long-term maintainability.',
      headings: [
        'Backend-first thinking with UX in mind',
        'Performance, scalability, and security by default',
        'Clear architecture, clean abstractions, no over-engineering',
        'Built for growth, not just delivery',
      ],
      shape: `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
  <path d="M127.14 200C99.9942 200 99.9943 167.423 72.8487 167.423C41.6048 167.423 0 158.386 0 127.133C0 99.9885 32.5678 99.9885 32.5678 72.8445C32.5678 41.6139 41.6048 0 72.8602 0C100.006 0 100.006 32.5774 127.151 32.5774C158.384 32.5774 200 41.6139 200 72.8675C200 100.012 167.421 100.012 167.421 127.156C167.409 158.444 158.384 200 127.14 200Z" />
  </svg>
  `,
    },
    {
      title: 'Architecture & APIs',
      body: 'I design scalable backend architectures and clean, secure APIs that prioritize performance, maintainability, and real-world reliability.I create custom-coded websites specifically for your brand. I focus on making sure they are scalable, fast, accessible, and have engaging animations to provide a memorable experience for users.',
      headings: [
        'Scalable REST & GraphQL APIs',
        'Backend architecture with Node.js & NestJS',
        'Secure authentication & authorization systems',
        'High-performance database designs & queries',
        'Clean, maintainable, production-grade code',
      ],
      shape: `<svg viewBox="0 0 200 200"  xmlns="http://www.w3.org/2000/svg">
  <path d="M100 0C103.395 53.7596 146.24 96.6052 200 100C146.24 103.395 103.395 146.24 100 200C96.6052 146.24 53.7596 103.395 0 100C53.7596 96.6052 96.6052 53.7596 100 0Z"/>
  </svg>
  `,
    },
    {
      title: 'Backend Development',
      body: 'skills',
      headings: [
        'Node.js',
        'NestJS',
        'Express.js',
        'RESTful API Design',
        'Microservices Architecture',
        'Event-Driven Architecture',
        'Authentication & Authorization (JWT, OAuth)',
      ],
      shape: `<svg  viewBox="0 0 200 200"  xmlns="http://www.w3.org/2000/svg">
  <path d="M100 0L170.711 29.2893L200 100L170.711 170.711L100 200L29.2893 170.711L0 100L29.2893 29.2893L100 0Z" />
  </svg>
  `,
    },
    {
      title: 'Databases & Data Management',
      body: 'skills',
      headings: [
        'PostgreSQL',
        'MySQL',
        'MongoDB',
        'Indexing & Query Optimization',
        'Database Design',
      ],
      shape: `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
  <path d="M127.14 200C99.9942 200 99.9943 167.423 72.8487 167.423C41.6048 167.423 0 158.386 0 127.133C0 99.9885 32.5678 99.9885 32.5678 72.8445C32.5678 41.6139 41.6048 0 72.8602 0C100.006 0 100.006 32.5774 127.151 32.5774C158.384 32.5774 200 41.6139 200 72.8675C200 100.012 167.421 100.012 167.421 127.156C167.409 158.444 158.384 200 127.14 200Z" />
  </svg>`,
    },
    {
      title: 'Cloud & Infrastructure',
      body: 'skills',
      headings: [
        'AWS (Lambda, S3, EC2, SNS, EventBridge, CloudWatch)',
        'CI/CD Pipelines',
        'GCP',
      ],
      shape: `<svg viewBox="0 0 200 200"  xmlns="http://www.w3.org/2000/svg">
  <path d="M100 0C103.395 53.7596 146.24 96.6052 200 100C146.24 103.395 103.395 146.24 100 200C96.6052 146.24 53.7596 103.395 0 100C53.7596 96.6052 96.6052 53.7596 100 0Z"/>
  </svg>
  `,
    },
    {
      title: 'Performance & Scalability',
      body: 'skills',
      headings: ['Load Handling & Optimization', 'Monitoring & Logging'],
      shape: `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
  <path d="M127.14 200C99.9942 200 99.9943 167.423 72.8487 167.423C41.6048 167.423 0 158.386 0 127.133C0 99.9885 32.5678 99.9885 32.5678 72.8445C32.5678 41.6139 41.6048 0 72.8602 0C100.006 0 100.006 32.5774 127.151 32.5774C158.384 32.5774 200 41.6139 200 72.8675C200 100.012 167.421 100.012 167.421 127.156C167.409 158.444 158.384 200 127.14 200Z" />
  </svg>
  `,
    },
    {
      title: 'Security & Testing',
      body: 'skills',
      headings: [
        'API Security Best Practices',
        'Input Validation & Sanitization',
        'Data Encryption',
        'Code Reviews & PR Best Practices',
        'Clean Code & SOLID Principles',
      ],
      shape: `<svg viewBox="0 0 200 200"  xmlns="http://www.w3.org/2000/svg">
  <path d="M100 0C103.395 53.7596 146.24 96.6052 200 100C146.24 103.395 103.395 146.24 100 200C96.6052 146.24 53.7596 103.395 0 100C53.7596 96.6052 96.6052 53.7596 100 0Z"/>
  </svg>
  `,
    },
    {
      title: 'Tools & Workflow',
      body: 'skills',
      headings: [
        'Git & GitHub',
        'Swagger / OpenAPI Documentation',
        'Docker (Basics)',
        'Agile & Scrum Collaboration',
      ],
      shape: `<svg  viewBox="0 0 200 200"  xmlns="http://www.w3.org/2000/svg">
  <path d="M100 0L170.711 29.2893L200 100L170.711 170.711L100 200L29.2893 170.711L0 100L29.2893 29.2893L100 0Z" />
  </svg>
  `,
    },
  ];

  onBeforeMount(() => {
    whatIDo.value = textSplitterIntoChar('What I Build', true);
  });

  onMounted(() => {
    animateSplitText('#what-i-do .letters', '#services-text', 0.7, 0.01, 0);
  });

  const getStyle = (index: number) => {
    if (index === 0) {
      return 'top-[calc(20vh_+_0em)] mb-[5.75em]';
    }

    if (index === 1) {
      return 'top-[calc(20vh_+_0em)] mb-[5.75em]';
    }

    if (index === 2) {
      return 'top-[calc(20vh_+_0em)] mb-[5.75em]';
    }
    if (index === 3) {
      return 'top-[calc(20vh_+_0em)] mb-[5.75em]';
    }
    if (index === 4) {
      return 'top-[calc(20vh_+_0em)] mb-[5.75em]';
    }
    if (index === 5) {
      return 'top-[calc(20vh_+_0em)] mb-[5.75em]';
    }
    if (index === 6) {
      return 'top-[calc(20vh_+_0em)] mb-[5.75em]';
    }
    if (index === 7) {
      return 'top-[calc(20vh_+_0em)] mb-[5.75em]';
    }
  };
</script>
